<template>
    <div class="relative" style="min-width: 738px;">
        <div class="mb-5 mr-4 pb-2 flex justify-between border-b border-gray-300">
            <div v-if="showHeader" class="flex items-center" :class="{ 'my-3': smallHeader }">
                <h1 class="module-header" :class="{ 'text-2xl': smallHeader }">Project Sites</h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div :class="{ 'text-2xl': smallHeader }">/</div>
                    <div class="ml-2 text-xl" :class="[smallHeader ? 'pt-2px' : 'pt-1']">{{ projectSites.filtered.length }} of {{ projectSites.all.length }}</div>
                </div>
            </div>
            <div v-if="showFilters" class="flex items-center" :class="{'flex-1 justify-end pt-4 pb-3': ! showHeader}">
                <filters
                    data-type="projectSites"
                    sort-by="building.address_line_1"
                    :selected-view-option="layout"
                    portal="no-project-sites-found"
                    @view-change="layout = $event">
                </filters>
                <slot name="action-buttons"></slot>
            </div>
        </div>

        <div v-if="layout == 'grid'">
            <paginate name="sites" :list="projectSites.filtered" :per="perPage" class="paginate-list">
                <div class="mt-5 w-full grid grid-cols-1 lg:grid-cols-2 gap-6" :class="{ 'mx-auto': centerCards }">
                    <div class="card" v-for="site in paginated('sites')" :key="site.id">
                        <a
                            :key="site.id"
                            class="h-full relative flex items-stretch text-gray-800 cursor-pointer transform hover:no-underline hover:scale-105"
                            :href="site.view_url"
                        >
                            <div class="w-full flex border rounded-lg shadow-lg">
                                <div class="w-full flex flex-col">
                                    <div class="flex flex-1">
                                        <div class="absolute top-0 right-0 badge border rounded rounded-br-none tr-sm" :class="site.status_color">
                                            <div class="flex items-baseline font-variant-all-small-caps space-x-1">
                                                <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                                <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                                <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                                <div v-else>No Lease Information</div>
                                            </div>
                                        </div>
                                        <div
                                            class="flex w-full"
                                            :class="{ 'bg-red-500 text-white': site.is_upcoming }"
                                        >
                                            <div
                                                class="w-40 h-full relative"
                                                :class="site.is_upcoming ? 'text-red-500' : 'text-white font-semibold'"
                                            >
                                                <div v-if="site.archived_at !== null" class="mt-4 ml-4 absolute top-0 left-0 z-40" title="Site is Archived">
                                                    <inline-svg name="archived"></inline-svg>
                                                </div>
                                                <img class="w-full h-full border-r border-white rounded-tl-lg" :src="site.building.image.view_url">
                                                <inline-svg name="image-skewer-right"></inline-svg>
                                            </div>
                                            <div class="mx-4 mt-12 mb-10 flex flex-1 flex-col justify-center">
                                                <div class="uppercase">
                                                    <p class="text-lg font-semibold" :title="site.building.address_line_1">{{ site.building.address_line_1 | truncate(25) }}</p>
                                                    <p class="text-sm">{{ site.building.city }}, {{ site.building.state }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="py-4 px-5 flex justify-between bg-gray-100 rounded-b-lg">
                                        <div>
                                            <div v-if="site.utilization" class="badge" :class="site.utilization_color">
                                                <div class="flex items-baseline space-x-1">
                                                    <span class="font-extrabold">{{ site.utilization }}%</span>
                                                    <span class="text-base font-variant-all-small-caps">Utilized</span>
                                                </div>
                                            </div>

                                            <div v-else></div>
                                        </div>

                                        <div class="badge badge-red">
                                            <div class="flex items-baseline space-x-1">
                                                <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                                <span class="text-base font-variant-all-small-caps">Remaining</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </paginate>
            <paginate-links for="sites" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <div v-if="layout == 'table' && projectSites.filtered.length">
            <table class="w-full">
                <thead class="cursor-pointer">
                    <tr>
                        <th class="pl-4 hidden lg:table-cell"></th>
                        <th class="pl-4 lg:pl-0">Address</th>
                        <th>Lease or Own</th>
                        <th class="text-center">Due In</th>
                        <th class="text-right">Lease Cost</th>
                        <th class="pr-4 lg:pr-0 text-right">Remaining</th>
                        <th class="hidden lg:table-cell text-right">Current PSF</th>
                        <th class="hidden lg:table-cell text-right">Utilization</th>
                    </tr>
                </thead>
                <paginate name="sites" :list="projectSites.filtered" :per="perPage" tag="tbody">
                    <tr
                        v-for="site in paginated('sites')" :key="site.id"
                        :class="{
                            'hidden': ! showArchivedSites && site.archived_at !== null,
                            'row-red': site.is_upcoming
                        }"
                    >
                        <td class="pl-4 hidden lg:table-cell relative h-16">
                            <div v-if="site.archived_at !== null" class="absolute mt-1 ml-1 z-40" title="Site is Archived">
                                <inline-svg name="archived" classes="w-2 h-2 text-white fill-current"></inline-svg>
                            </div>
                            <a :href="`#${site.id}`">
                                <img :src="site.building.image.view_url" class="mx-auto w-12 h-12 rounded-md shadow-md">
                            </a>
                            <a href="#_" class="lightbox" :id="`${site.id}`">
                                <img :src="site.building.image.view_url">
                            </a>
                        </td>
                        <td class="pl-4 lg:pl-0 leading-normal">
                            <a v-bind:href="site.view_url">
                                <p class="text-sm font-semibold">{{ site.building.address_line_1 }}</p>
                                <p class="text-xs">{{ site.building.city }}, {{ site.building.state }}</p>
                            </a>
                        </td>
                        <td :class="{ 'text-white font-semibold': site.is_upcoming }">{{ site.lease_or_own }}</td>
                        <td class="text-center">
                            <div class="badge" :class="site.status_color">
                                <div class="flex items-baseline text-base font-variant-all-small-caps space-x-1">
                                    <div v-if="site.lease_or_own == 'Owned'">Owned</div>
                                    <div v-else-if="site.leases.length && site.due_in_days > 0">Due in <span class="font-bold">{{ site.due_in_text }}</span></div>
                                    <div v-else-if="site.leases.length && site.due_in_days == 0">Expired</div>
                                    <div v-else>No Lease Information</div>
                                </div>
                            </div>
                        </td>
                        <td data-precision="0" :data-originalvalue="site.lease_cost" class="is-currency text-right" :class="{ 'text-white font-semibold': site.is_upcoming }">{{ formatCurrency(site.lease_cost) }}</td>
                        <td class="pr-4 lg:pr-0 text-right">
                            <div class="badge badge-red">
                                <div class="flex items-baseline space-x-1">
                                    <span :data-originalvalue="site.shortened_remaining_lease_cost" class="is-shorthand-currency font-extrabold">${{ site.shortened_remaining_lease_cost }}</span>
                                </div>
                            </div>
                        </td>
                        <td data-precision="2" :data-originalvalue="site.current_psf" class="is-currency hidden lg:table-cell text-right" :class="{ 'text-white font-semibold': site.is_upcoming }">{{ formatCurrency(site.current_psf, 2) }}</td>
                        <td class="hidden lg:table-cell text-right font-semibold">
                            <div v-if="site.utilization" class="badge" :class="site.utilization_color">
                                <div class="flex items-baseline space-x-1">
                                    <span class="font-extrabold">{{ site.utilization }}%</span>
                                    <span class="text-base font-variant-all-small-caps">Utilized</span>
                                </div>
                            </div>
                            <div v-else>-</div>
                        </td>
                    </tr>
                </paginate>
            </table>
            <paginate-links for="sites" :show-step-links="true" :hide-single-page="true"></paginate-links>
        </div>

        <div v-if="! projectSites.filtered.length">
            <p class="none-found">No Project Sites Found</p>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import { mapState } from 'vuex';

    export default {
        props: {
            siteData: {
              type: Array,
              required: true
            },

            showHeader: {
              type: Boolean,
              default: true
            },

            smallHeader: {
              type: Boolean,
              default: false
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            showArchivedSites: {
                type: Boolean,
                default: false
            },

            centerCards: {
                type: Boolean,
                default: false
            },

            view: {
                type: String,
                default: 'grid'
            },

            perPage: {
                type: Number,
                default: 10
            }
        },

        data() {
            return {
                paginate: ['sites'],
                layout: this.view
            }
        },

        created() {
            if (localStorage.getItem('reopt_projectSites_layout')) {
                this.layout = localStorage.getItem('reopt_projectSites_layout');
            }

            this.projectSites.all = this.siteData;
            this.projectSites.filtered = this.siteData;            
        },

        computed: {
            ...mapState(['projectSites']),
        },

        methods: {
            dueIn(lease_end_date) {
                return moment(lease_end_date).isBefore(moment()) ? 'Expired' : 'Due ' + moment(lease_end_date).fromNow()
            },
        }
    }
</script>